import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, MessageCircle, Star } from "lucide-react";
import { Logo } from "@/components/Logo";
import { BUSINESS, telLink, mailLink, whatsappLink, NAV_LINKS } from "@/data/business";
import { SERVICE_AREAS } from "@/data/suburbs";

export function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-navy text-white/80" data-testid="site-footer">
      <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 py-16">
        <div className="grid gap-12 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <Logo variant="light" />
            <p className="mt-5 text-sm leading-relaxed text-white/70">
              Professional house cleaning across West Melbourne, East Melbourne and North Melbourne.
              Reliable, friendly and detail-focused — so you can enjoy a cleaner, fresher home without the stress.
            </p>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-brandgreen px-5 py-2.5 text-sm font-semibold text-white hover:bg-brandgreen-dark transition-colors"
              data-testid="footer-whatsapp"
            >
              <MessageCircle className="w-4 h-4" /> Message us on WhatsApp
            </a>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-white text-sm tracking-wide uppercase">Quick Links</h4>
            <ul className="mt-5 space-y-3 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-brandgreen-light transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/quote" className="hover:text-brandgreen-light transition-colors">Get a Quote</Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-white text-sm tracking-wide uppercase">Service Areas</h4>
            <ul className="mt-5 space-y-3 text-sm">
              {SERVICE_AREAS.map((s) => (
                <li key={s.name} className="flex items-center gap-2 text-white/80">
                  <MapPin className="w-3.5 h-3.5 text-brandgreen shrink-0" /> {s.name}
                </li>
              ))}
              <li className="pt-1">
                <Link to="/service-areas" className="hover:text-brandgreen-light transition-colors font-medium">
                  About our service areas →
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-white text-sm tracking-wide uppercase">Get In Touch</h4>
            <ul className="mt-5 space-y-4 text-sm">
              <li>
                <a href={telLink} className="flex items-start gap-3 hover:text-brandgreen-light transition-colors" data-testid="footer-call">
                  <Phone className="w-4 h-4 mt-0.5 text-brandgreen" /> {BUSINESS.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={mailLink} className="flex items-start gap-3 hover:text-brandgreen-light transition-colors break-all">
                  <Mail className="w-4 h-4 mt-0.5 text-brandgreen" /> {BUSINESS.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-brandgreen" /> {BUSINESS.region}
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 mt-0.5 text-brandgreen" />
                <span>Mon–Fri: 9:00 AM – 4:30 PM<br />Sat & Sun: Closed</span>
              </li>
            </ul>
            <div className="mt-5 flex items-center gap-2 rounded-xl bg-white/5 px-4 py-3 text-xs text-white/60">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              Google Business Profile — connecting soon
            </div>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/60">
          <p>© {year} {BUSINESS.name}. ABN {BUSINESS.abn}. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
            <Link to="/cancellation-policy" className="hover:text-white transition-colors">Cancellation Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
